import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { getStoredRole, getTopNavLinks } from '../utils/navigation';

const C = { bg: '#0A0E1A', surface: '#111827', border: '#1F2937', accent: '#10B981', text: '#F9FAFB', muted: '#6B7280' };
const s = {
  page: { minHeight: '100vh', background: C.bg, color: C.text, fontFamily: "'DM Sans','Segoe UI',sans-serif" },
  topbar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 32px', borderBottom: `1px solid ${C.border}`, background: C.surface },
  logo: { fontSize: 20, fontWeight: 700, color: C.accent },
  nav: { display: 'flex', gap: 24, flexWrap: 'wrap' },
  navLink: (active) => ({ color: active ? C.accent : C.muted, textDecoration: 'none', fontSize: 14, fontWeight: active ? 700 : 400 }),
  main: { padding: 32, maxWidth: 1200, margin: '0 auto' },
  heading: { fontSize: 26, fontWeight: 700, marginBottom: 4 },
  sub: { fontSize: 14, color: C.muted, marginBottom: 24 },
  card: { background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, overflow: 'hidden' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', fontSize: 11, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.06em', padding: '12px 16px', borderBottom: `1px solid ${C.border}` },
  td: { padding: '12px 16px', fontSize: 13, borderBottom: `1px solid ${C.border}22` },
  empty: { textAlign: 'center', padding: '48px 0', color: C.muted }
};

export default function Accounts() {
  const [items, setItems] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const [createSuccess, setCreateSuccess] = useState('');
  const [form, setForm] = useState({ member_id: '', account_type: 'savings', currency: 'USD' });
  const role = getStoredRole();
  const links = getTopNavLinks(role);
  const canCreate = ['superadmin', 'admin', 'manager'].includes(role);

  const inputStyle = { background: '#1C2433', border: `1px solid ${C.border}`, color: C.text, borderRadius: 8, padding: '10px 12px', fontSize: 13, width: '100%', boxSizing: 'border-box' };

  const loadAccounts = async () => {
    const r = await api.get('/accounts');
    setItems(Array.isArray(r.data) ? r.data : []);
  };

  const loadMembers = async () => {
    const r = await api.get('/members');
    const list = Array.isArray(r.data) ? r.data : [];
    setMembers(list);
    if (!form.member_id && list.length > 0) {
      setForm((prev) => ({ ...prev, member_id: String(list[0].id) }));
    }
  };

  useEffect(() => {
    const load = async () => {
      try {
        await loadAccounts();
        if (canCreate) {
          await loadMembers();
        }
      } catch {
        setItems([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [canCreate]);

  const handleCreate = async (event) => {
    event.preventDefault();
    if (!form.member_id || !form.account_type) {
      setCreateError('Selecciona miembro y tipo de cuenta');
      return;
    }

    setCreating(true);
    setCreateError('');
    setCreateSuccess('');

    try {
      const payload = {
        member_id: Number(form.member_id),
        account_type: form.account_type,
        currency: form.currency || 'USD',
      };

      const response = await api.post('/accounts', payload);
      await loadAccounts();
      setCreateSuccess(`Cuenta creada: ${response?.data?.account_number || 'OK'}`);
    } catch (error) {
      const msg = error?.response?.data?.error || 'No se pudo crear la cuenta';
      setCreateError(msg);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div style={s.page}>
      <div style={s.topbar}>
        <div style={s.logo}>⚡ FintechCoop</div>
        <nav style={s.nav}>{links.map(link => <Link key={link.to} to={link.to} style={s.navLink(link.to === '/accounts')}>{link.label}</Link>)}</nav>
      </div>
      <div style={s.main}>
        <div style={s.heading}>Cuentas</div>
        <div style={s.sub}>Cuentas operativas y balances disponibles</div>

        {canCreate && (
          <div style={{ ...s.card, marginBottom: 16, padding: 16 }}>
            <form onSubmit={handleCreate}>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr auto', gap: 12, alignItems: 'end' }}>
                <div>
                  <div style={{ fontSize: 12, color: C.muted, marginBottom: 6 }}>Miembro</div>
                  <select
                    value={form.member_id}
                    onChange={(e) => setForm((prev) => ({ ...prev, member_id: e.target.value }))}
                    style={inputStyle}
                    required
                  >
                    {members.map((member) => (
                      <option key={member.id} value={member.id}>
                        {member.full_name || 'Sin nombre'} #{member.id}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <div style={{ fontSize: 12, color: C.muted, marginBottom: 6 }}>Tipo</div>
                  <select
                    value={form.account_type}
                    onChange={(e) => setForm((prev) => ({ ...prev, account_type: e.target.value }))}
                    style={inputStyle}
                  >
                    <option value="savings">Ahorros</option>
                    <option value="checking">Corriente</option>
                    <option value="wallet">Wallet</option>
                  </select>
                </div>

                <div>
                  <div style={{ fontSize: 12, color: C.muted, marginBottom: 6 }}>Moneda</div>
                  <input
                    value={form.currency}
                    onChange={(e) => setForm((prev) => ({ ...prev, currency: e.target.value.toUpperCase().slice(0, 3) }))}
                    style={inputStyle}
                    placeholder="USD"
                  />
                </div>

                <button
                  type="submit"
                  disabled={creating || members.length === 0}
                  style={{ background: C.accent, color: '#fff', border: 'none', borderRadius: 8, padding: '10px 14px', fontWeight: 700, cursor: creating ? 'not-allowed' : 'pointer', opacity: creating ? 0.7 : 1 }}
                >
                  {creating ? 'Creando...' : 'Crear cuenta'}
                </button>
              </div>
              {createError && <div style={{ marginTop: 10, color: '#EF4444', fontSize: 13 }}>{createError}</div>}
              {createSuccess && <div style={{ marginTop: 10, color: C.accent, fontSize: 13 }}>{createSuccess}</div>}
            </form>
          </div>
        )}

        <div style={s.card}>
          {loading ? <div style={s.empty}>Cargando cuentas...</div> : items.length === 0 ? <div style={s.empty}>Sin datos disponibles</div> : (
            <table style={s.table}>
              <thead><tr>{['ID', 'Número', 'Tipo', 'Balance', 'Estado'].map(h => <th key={h} style={s.th}>{h}</th>)}</tr></thead>
              <tbody>
                {items.map((a) => (
                  <tr key={a.id}>
                    <td style={s.td}>{a.id}</td>
                    <td style={s.td}>{a.account_number || '—'}</td>
                    <td style={s.td}>{a.account_type || '—'}</td>
                    <td style={s.td}>{a.balance ?? '—'}</td>
                    <td style={s.td}>{a.status || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
